import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Loader2, AlertCircle, CheckCircle, Mail } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/** Step 1 of 5 — Account. If already signed in, just confirms identity and moves on.
 *  If not, offers Google OR an inline email+password create-account form. */
const StepAccount = ({ onNext }) => {
  const { user, login, setAuthUser } = useAuth();
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [form, setForm] = useState({ firstName: '', surname: '', email: '', password: '', confirmPassword: '' });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleGoogle = () => {
    sessionStorage.setItem('postLoginRedirect', '/register-professional');
    login();
  };

  const handleCreateAccount = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (!agreed) {
      setError('Please agree to the Terms of Service and Privacy Policy.');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/register`,
        { name: `${form.firstName} ${form.surname}`.trim(), email: form.email, password: form.password },
        { withCredentials: true }
      );
      setAuthUser(res.data);
      onNext();
    } catch (e2) {
      setError(e2.response?.data?.detail || 'Could not create your account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (user) {
    return (
      <div className="space-y-5" data-testid="step-account-signed-in">
        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-4">
          <CheckCircle size={22} className="text-[#37b34a] flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-gray-900">Signed in as {user.name}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={onNext}
          data-testid="account-continue-btn"
          className="w-full bg-[#37b34a] hover:bg-[#2d9a3e] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
        >
          Continue
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <p className="text-sm text-gray-600">Create your provider account to continue registration.</p>

      {error && (
        <div data-testid="account-error" className="bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-lg flex items-start gap-2">
          <AlertCircle size={16} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      <button
        type="button"
        onClick={handleGoogle}
        data-testid="account-google-btn"
        className="w-full flex items-center justify-center gap-2 border-2 border-gray-200 hover:border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold px-6 py-3 rounded-lg transition-colors"
      >
        Continue with Google
      </button>

      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-gray-200" />
        <span className="text-xs text-gray-400 font-medium">OR</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {!showPasswordForm ? (
        <button
          type="button"
          onClick={() => setShowPasswordForm(true)}
          data-testid="show-email-signup-btn"
          className="w-full flex items-center justify-center gap-2 text-sm font-semibold text-[#37b34a] hover:text-[#2d9a3e] py-2"
        >
          <Mail size={16} /> Create an account with email &amp; password
        </button>
      ) : (
        <form onSubmit={handleCreateAccount} className="space-y-3" data-testid="email-signup-form">
          <div className="grid grid-cols-2 gap-3">
            <input
              type="text" required placeholder="First name(s)" data-testid="account-firstname-input"
              value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
            />
            <input
              type="text" required placeholder="Surname" data-testid="account-surname-input"
              value={form.surname} onChange={(e) => setForm({ ...form, surname: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
            />
          </div>
          <input
            type="email" required placeholder="Email address" data-testid="account-email-input"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
          />
          <div className="grid grid-cols-2 gap-3">
            <input
              type="password" required minLength={6} placeholder="Password (6+ chars)" data-testid="account-password-input"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
            />
            <input
              type="password" required minLength={6} placeholder="Confirm password" data-testid="account-confirm-password-input"
              value={form.confirmPassword} onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#37b34a] focus:border-transparent outline-none"
            />
          </div>
          <label className="flex items-start gap-2.5 text-xs text-gray-600 cursor-pointer select-none">
            <input
              type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
              data-testid="account-tos-checkbox" className="mt-0.5 accent-[#37b34a]"
            />
            <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="underline font-medium">Terms of Service</a> and <a href="/privacy" target="_blank" rel="noreferrer" className="underline font-medium">Privacy Policy</a>.</span>
          </label>
          <button
            type="submit" disabled={loading} data-testid="account-create-btn"
            className="w-full bg-[#37b34a] hover:bg-[#2d9a3e] disabled:bg-gray-300 text-white font-semibold px-6 py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            Create Account &amp; Continue
          </button>
        </form>
      )}
    </div>
  );
};

export default StepAccount;
